import { ref, computed } from 'vue'
import { deepClone } from '@/utils/helpers'
import { IRequestPayload } from '@/api/types'

import { LIMIT } from '@/constants'

const useCrudStore = ({
  defaultFilter = {},
  fetchItems = (payload: IRequestPayload, variantType?: string) =>
    Promise.resolve(),
  updateItem = (_arg: any) => Promise.resolve(),
  createItem = (_arg: any) => Promise.resolve(),
  fetchItem = (id: string) => Promise.resolve(),
  destroyItem = (id: string) => Promise.resolve()
}: {
  defaultFilter?: any
  fetchItems?: (payload: IRequestPayload, variantType?: string) => Promise<any>
  updateItem?: (payload: any) => Promise<any>
  createItem?: (payload: any) => Promise<any>
  fetchItem?: (id: string) => Promise<any>
  destroyItem?: (id: string) => Promise<any>
}) => {
  const items = ref<{ [itemId: string]: any }>({})
  const total = ref(0)
  const currentItems = ref<any[]>([])
  const limit = ref(LIMIT)
  const start = ref(0)
  const sortField = ref('id')
  const sortDirection = ref('desc')
  const currentPage = ref(1)
  const listLoading = ref(false)
  const current = ref<any>(null)
  const itemsLoading = ref<{ [itemId: string]: boolean }>({})
  const expanded = ref(false)
  const closed = ref<{ [itemId: string]: any }>({})
  const variant = ref<{ [itemId: string]: any[] }>({})
  const selected = ref<{ [itemId: string]: any }>({})
  const filter = ref<any>(deepClone(defaultFilter))
  const error = ref<string | null>(null)

  // Actions
  const SetFilters = (newFilter: any) => {
    filter.value = { ...newFilter }
  }

  const SetCurrentPage = (page: number) => {
    currentPage.value = page
  }

  const SetExpanded = (indicator: boolean) => {
    expanded.value = indicator
  }

  const ToggleClose = async ({
    id,
    isClosed
  }: {
    id: string
    isClosed: boolean
  }) => {
    if (isClosed) {
      closed.value[id] = items.value[id]
    } else {
      delete closed.value[id]
    }
  }

  const ToggleSelection = (id: string, isSelected: boolean) => {
    if (isSelected) {
      selected.value[id] = items.value[id]
    } else {
      delete selected.value[id]
    }
  }

  const ClearSelected = () => {
    selected.value = {}
  }

  const SetItems = (newItems: any[]) => {
    newItems.forEach((item) => {
      setItem(item)
    })
  }

  const GetItems = async () => {
    if (Object.keys(items.value).length === 0) {
      await FetchItems()
    }
  }

  const FetchItems = async (currentPayload: undefined | any = undefined) => {
    currentPayload = currentPayload || payload.value
    listLoading.value = true
    error.value = null
    try {
      const response = await fetchItems(currentPayload)
      setItems(response.data)
      setCurrentItems(response.data)
      setTotal(response.meta.total)
      return response.data
    } catch (e: any) {
      console.error(e)
      error.value = e.toString()
      return []
    } finally {
      listLoading.value = false
      error.value = null
    }
  }

  const FetchItem = async (id: string) => {
    itemsLoading.value[id] = true
    error.value = null
    try {
      const response = await fetchItem(id)
      setCurrent(response.data)
      setItem(response.data)
      return response
    } catch (e: any) {
      console.error(e)
      error.value = e.toString()
    } finally {
      itemsLoading.value = {}
      error.value = null
    }
  }

  const SaveItem = async (item: any) => {
    if (!item) return
    const response = await (item.id ? updateItem(item) : createItem(item))
    setCurrent(null)
    return Object.prototype.hasOwnProperty.call(response, 'data')
      ? response.data
      : response
  }

  const DestroyItem = async (id: string) => {
    itemsLoading.value[id] = true
    try {
      await destroyItem(id)
      removeItem(id)
    } catch (e: any) {
      console.error(e)
      error.value = e.toString()
    } finally {
      itemsLoading.value = {}
      error.value = null
    }
  }

  const FetchItemsVariant = async (variantType: string, payload?: any) => {
    payload = payload || { start: 0, limit: 1000 }
    listLoading.value = true
    error.value = null
    try {
      const response = await fetchItems(payload, variantType)
      setVariantItems(variantType, response.data)
      return response.data
    } catch (e: any) {
      console.error(e)
      error.value = e.toString()
    } finally {
      listLoading.value = false
      error.value = null
    }
  }

  const SetCurrent = (item: any) => {
    setCurrent(item)
  }

  const SetSelection = (items: any[]) => {
    selected.value = items.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  }

  const SetLimit = (newLimit: number) => {
    limit.value = newLimit
  }

  const SetSortField = (field: string) => {
    sortField.value = field
  }

  const SetSortDirection = (direction: string) => {
    sortDirection.value = direction
  }

  const setCurrentItems = (data: any[]) => {
    currentItems.value = data
  }

  const setItems = (data: any[]) => {
    items.value = data.reduce((acc: { [key: string]: any }, item: any) => {
      acc[item.id] = item
      return acc
    }, {})
  }

  const setTotal = (number: number) => {
    total.value = number
  }

  const setCurrent = (record: any) => {
    current.value = record?.attributes
  }

  const setItem = (record: any) => {
    let updatedItem = { ...record }
    const existedItem = items.value[record.id]

    if (existedItem) {
      updatedItem = {
        ...existedItem,
        attributes: {
          ...existedItem.attributes,
          ...record.attributes
        }
      }
    }

    items.value[record.id] = updatedItem

    // Find the index of the item in the array
    const index = currentItems.value.findIndex(
      (element) => element.id === record.id
    )

    // If the item exists, merge the attributes and update the array
    if (index !== -1) {
      updatedItem = {
        ...currentItems.value[index],
        attributes: {
          ...currentItems.value[index].attributes,
          ...record.attributes
        }
      }

      // Replace the old item with the updated one
      currentItems.value[index] = updatedItem
    }
  }

  const setVariantItems = (variantType: string, data: any[]) => {
    variant.value[variantType] = data
  }

  const removeItem = (id: string) => {
    delete items.value[id]
    currentItems.value = currentItems.value.filter((item) => item.id !== id)
  }

  const payload = computed(() => ({
    limit: limit.value,
    start: (currentPage.value - 1) * limit.value,
    sorter: {
      field: sortField.value,
      direction: sortDirection.value
    },
    filter: filter.value
  }))

  // Getters
  const itemById = computed(() => (id: string, variantType?: string) => {
    let item: any | undefined
    if (variantType && variant.value[variantType]) {
      item = variant.value[variantType].find((el: any) => el.id === id)
    } else {
      item = items.value[id]
    }
    if (item) {
      return JSON.parse(JSON.stringify(item.attributes)) as any
    } else {
      return null
    }
  })

  const sorted = computed(() => {
    return Object.values(items.value).sort((a, b) => {
      const nameA = a.attributes.name || ''
      const nameB = b.attributes.name || ''
      return nameA.localeCompare(nameB)
    })
  })

  const currentSorted = computed(() => {
    return currentItems.value.sort((a, b) => {
      const nameA = a.attributes.name || ''
      const nameB = b.attributes.name || ''
      return nameA.localeCompare(nameB)
    })
  })

  const getVariantItems = computed(() => (variantType: string): any[] => {
    return variant.value[variantType] || []
  })

  return {
    // Actions
    SetFilters,
    SetCurrentPage,
    SetExpanded,
    SetItems,
    ToggleClose,
    ToggleSelection,
    GetItems,
    FetchItems,
    FetchItemsVariant,
    FetchItem,
    SaveItem,
    DestroyItem,
    ClearSelected,
    SetCurrent,
    SetSelection,
    SetLimit,
    SetSortField,
    SetSortDirection,

    items,
    current,
    currentItems,
    total,
    limit,
    start,
    sortField,
    sortDirection,
    currentPage,
    listLoading,
    itemsLoading,
    expanded,
    closed,
    variant,
    selected,
    filter,
    error,

    // Getters
    itemById,
    getVariantItems,
    sorted,
    payload,
    currentSorted,

    // private
    setItems,
    setCurrentItems,
    setTotal,
    setCurrent,
    setItem,
    removeItem
  }
}

export default useCrudStore
